import React from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Edit } from '@mui/icons-material';

import { Popup } from '@commonComponents';
import { updateTenant } from '@api-admin/tenants';
import { TenantType } from '@types';
import toast from '@utils/toast';
import { cleanEntityMetadata } from '@utils/metadata';
import { formatDate, formatSubscriptionDate } from '@utils/parsing';
import { Input } from '@styledComponents';

type PropsType = {
    entity: TenantType,
    onSaved: () => void
};

function isActiveSubscription(endDate: string | null) {
    if (!endDate) {
        return false;
    }
    return endDate.slice(0, 10) >= new Date().toISOString().slice(0, 10);
}

function getSubscriptionTitle(
    { subscriptionEndDate, activeSubscription }
        : { subscriptionEndDate: string | null, activeSubscription: boolean }
) {
    const commonTitle = 'Abonnemang tecknat t.o.m.';
    if (activeSubscription) {
        return `${commonTitle} (aktivt)`;
    }
    if (subscriptionEndDate) {
        return `${commonTitle} (utlöpt)`;
    }
    return `${commonTitle} (aldrig tecknat)`;
}

export default function SubscriptionEditor({ entity, onSaved }: PropsType) {
    const [open, setOpen] = React.useState(false);
    const [draftDate, setDraftDate] = React.useState<string | null>(entity.subscriptionEndDate);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const openEditor = React.useCallback(() => {
        setDraftDate(entity.subscriptionEndDate);
        setOpen(true);
    }, [entity.subscriptionEndDate]);

    const save = React.useCallback(async () => {
        setIsSubmitting(true);
        try {
            await updateTenant(entity.id, {
                ...cleanEntityMetadata(entity),
                subscriptionEndDate: draftDate,
                isCarrier: true
            });
            onSaved();
            toast('Abonnemang uppdaterat');
            setOpen(false);
        } catch (e) {
            toast(e, 'Kunde inte uppdatera abonnemanget, försök igen');
        }
        setIsSubmitting(false);
    }, [draftDate, entity, onSaved]);

    const displayText = formatSubscriptionDate({
        endDate: entity.subscriptionEndDate,
        isActive: entity.activeSubscription,
        isCarrier: entity.isCarrier
    }).replace('\n', ' ');

    return (
        <>
            <Popup
                open={open}
                title="Ändra abonnemang"
                handleOk={save}
                handleClose={() => setOpen(false)}
                isSubmitting={isSubmitting}
                okLabel="Spara"
                cancelLabel="Avbryt"
                fullWidth
                maxWidth="xs"
            >
                <DatePicker
                    label={getSubscriptionTitle({
                        subscriptionEndDate: draftDate,
                        activeSubscription: isActiveSubscription(draftDate)
                    })}
                    value={draftDate || null}
                    onChange={(newValue: string | Date | null) => {
                        setDraftDate(newValue ? formatDate(newValue) : null);
                    }}
                    mask="____-__-__"
                    renderInput={(params: any) => (
                        <Input
                            {...params}
                            fullWidth
                            inputProps={{ ...params.inputProps, placeholder: 'åååå-mm-dd' }}
                            autoComplete="off"
                        />
                    )}
                />
                {draftDate && (
                    <Button
                        variant="text"
                        color="error"
                        onClick={() => setDraftDate(null)}
                        disabled={isSubmitting}
                        sx={{ mt: 1 }}
                    >
                        Ta bort abonnemang
                    </Button>
                )}
            </Popup>
            <Box
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25
                }}
            >
                <Typography
                    variant="body2"
                    component="span"
                    sx={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
                    onClick={openEditor}
                >
                    {displayText}
                </Typography>
                <IconButton
                    aria-label="Ändra abonnemang"
                    size="small"
                    onClick={openEditor}
                    sx={{ p: 0.25, ml: 0.25 }}
                >
                    <Edit sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>
        </>
    );
}
